import Search from "@/assets/icons/Search";
import { usePermissions } from "@/auth/PermissionsContext";
import BadgeShared from "@/components/shared/Badge/Badge";
import CardCurrency from "@/components/shared/CardCurrency/CardCurrency";
import { ExportButton } from "@/components/shared/ExportButton";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Command, CommandInput } from "@/components/ui/command";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import LayoutInside from "@/layout/LayoutInside";
import { getOfferTypeLabel } from "@/lib/utils";
import useModalStore from "@/store/useModalStore";
import {
  formatDateForAPI,
  getDefaultDate,
  isValidDate,
} from "@/utils/dateHelpers";
import { getIconMethodPayment } from "@/utils/getIconMethodPayment";
import { ListFilter } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../Dashboard/Partials/DateRangePicker";
import HourlySalesChart from "../Dashboard/Partials/HourlySalesChart";
import FilterSales from "@/components/sales/FilterSales";
import {
  convertToSellsFilterParams,
  useSales,
} from "@/hooks/useSales";
import { resolveSaleDisplayDate } from "@/utils/resolveSaleDisplayDate";
import { resolveSaleSubscriptionLabel } from "@/utils/resolveSaleSubscriptionLabel";

export default function Sales() {
  const {
    dateRange,
    dateAxis,
    handleDateAxisChange,
    handleDateRangeChange,
    isLoadingPaidSales,
    getSalesPeriodText,
    isLoadingReports,
    paidSales,
    reports,
    sales,
    isLoadingSales,
    currentPage,
    searchQuery,
    setCurrentPage,
    setFilterParams,
    handleSearchChange,
    filterBadgeLabel,
    isFetchingSales,
    keyLabel,
    filterVisualParams,
    filterParams,
    hasActiveFiltersBeyondDates,
    removeFilterItem,
    removeFilter,
    clearAllFilters,
    currency,
    showPendingCard,
    showRejectedCard,
    showChargebackCard,
    showRefundedCard,
  } = useSales();

  const { t } = useTranslation(["common", "sales"]);
  const breadcrumbItems = [{ text: t("sales:sales"), link: "/sales" }];
  const navigate = useNavigate();
  const { modals, openModal, closeModal } = useModalStore();
  const { hasPermission } = usePermissions();
  const canViewDetails = hasPermission('order_details.view_order_details');

  const headers = [
    { title: t("sales:table.date"), key: "date" },
    { title: t("sales:table.product"), key: "transaction" },
    { title: t("sales:table.customer"), key: "client" },
    { title: t("sales:table.method"), key: "method" },
    { title: t("sales:table.value"), key: "value" },
    { title: t("sales:table.status"), key: "status" },
  ];

  const MAX_VISIBLE_FILTERS = 15;

  const exportStartDate = useMemo(() => {
    const fromDate = dateRange.from || getDefaultDate();
    return isValidDate(fromDate)
      ? formatDateForAPI(fromDate)
      : formatDateForAPI(getDefaultDate());
  }, [dateRange.from]);

  const exportEndDate = useMemo(() => {
    const toDate = dateRange.to || getDefaultDate();
    return isValidDate(toDate)
      ? formatDateForAPI(toDate)
      : formatDateForAPI(getDefaultDate());
  }, [dateRange.to]);

  const exportFilterParams = useMemo(() => {
    return convertToSellsFilterParams(filterParams, dateAxis);
  }, [filterParams, dateAxis]);

  const cardGridCols = useMemo(() => {
    const count = 1
      + (showPendingCard ? 1 : 0)
      + (showRejectedCard ? 1 : 0)
      + (showChargebackCard ? 1 : 0)
      + (showRefundedCard ? 1 : 0);
    if (count >= 3) return 'md:grid-cols-3';
    if (count === 2) return 'md:grid-cols-2';
    return 'md:grid-cols-1';
  }, [showPendingCard, showRejectedCard, showChargebackCard, showRefundedCard]);

  const allFilterBadges = useMemo(() => {
    const badges: Array<{
      key: string;
      group: string;
      label: string;
      onClick: () => void;
    }> = [];

    Object.entries(filterVisualParams).forEach(([key, values]) => {
      if (typeof values === "string") {
        if (values === "") return;
        badges.push({
          key,
          group: `${keyLabel[key]}:`,
          label: `${filterBadgeLabel[values] || values}`,
          onClick: () => removeFilter(key),
        });
        return;
      }

      if (!Array.isArray(values) || values.length === 0) return;

      if (values.every((item): item is string => typeof item === "string")) {
        values.forEach((value, index) => {
          badges.push({
            key: `${key}-${index}`,
            group: `${keyLabel[key]}:`,
            label: `${filterBadgeLabel[value] || value}`,
            onClick: () => removeFilterItem(key, index),
          });
        });
        return;
      }

      values.forEach((value, index) => {
        if (typeof value === "string") return;
        badges.push({
          key: `${key}-${index}`,
          group: `${keyLabel[key]}:`,
          label: `${value.label || value.value || ""}`,
          onClick: () => removeFilterItem(key, index),
        });
      });
    });

    return badges;
  }, [
    filterVisualParams,
    keyLabel,
    filterBadgeLabel,
    removeFilterItem,
    removeFilter,
  ]);

  return (
    <LayoutInside items={breadcrumbItems}>
      <CustomSheet
        open={modals["filter-sales"]}
        onClose={() => closeModal("filter-sales")}
      >
        <FilterSales
          filterValues={filterVisualParams}
          dateAxis={dateAxis}
          dateRange={dateRange}
          onDateAxisChange={handleDateAxisChange}
          onDateRangeChange={handleDateRangeChange}
            onFilter={(data) => {
            setCurrentPage(1);
            setFilterParams({
              products:
                data.products?.map((opt) => `${opt.label}:::${opt.value}`) ??
                [],
              offers:
                data.offers?.map((opt) => `${opt.label}:::${opt.value}`) ?? [],
              affiliates:
                data.affiliates?.map(
                  (opt) => `${opt.label}:::${opt.value}`,
                ) ?? [],
              coproducers:
                data.coproducers?.map(
                  (opt) => `${opt.label}:::${opt.value}`,
                ) ?? [],
              templates:
                data.templates?.map(
                  (opt) => `${opt.label}:::${opt.value}`,
                ) ?? [],
              payment_methods: data.payment_methods ?? [],
              status: data.status ?? [],
              src: data.src ?? "",
              utm_source: data.utm_source ?? "",
              utm_medium: data.utm_medium ?? "",
              utm_campaign: data.utm_campaign ?? "",
              utm_content: data.utm_content ?? "",
              utm_term: data.utm_term ?? "",
              ...(data.invoice_approved ? { invoice_approved: "1" as const } : {}),
              ...(data.invoice_not_approved ? { invoice_not_approved: "1" as const } : {}),
            });
          }}
        />
      </CustomSheet>

      <section>
        <Card className="p-3 justify-between">
          <div className="flex items-center gap-4">
            <div className="flex w-full flex-col md:flex-row justify-start gap-4">
              <div className="flex items-center justify-between flex-wrap max-[410px] gap-4">
                <div className="flex items-center gap-2">
                  <DateRangePicker
                    defaultValue={dateRange}
                    onChange={handleDateRangeChange}
                  />
                </div>
                <Button
                  className="flex md:hidden"
                  variant="secondary"
                  onClick={() => openModal("filter-sales")}
                >
                  <ListFilter size={24} />
                </Button>
              </div>

              <Command className="w-full">
                <CommandInput
                  defaultValue={searchQuery ?? ""}
                  placeholder={t("search_placeholder")}
                  onValueChange={handleSearchChange}
                  data-cy="search-sales"
                />
              </Command>
            </div>

            <Button
              className="hidden md:flex"
              variant="secondary"
              onClick={() => openModal("filter-sales")}
              data-cy="open-filter-sales"
            >
              <ListFilter size={24} />
              {t("sales:filter")}
            </Button>
          </div>

          {hasActiveFiltersBeyondDates && (
            <div className="flex flex-wrap gap-2 mt-4">
              {allFilterBadges.slice(0, MAX_VISIBLE_FILTERS).map((badge) => (
                <Badge
                  key={badge.key}
                  className="flex font-medium min-h-7 items-center hover:opacity-80 hover:bg-[#18181b] gap-4 bg-[#18181b] text-colorText"
                >
                  <div className="flex text-colorText items-center gap-1">
                    <span className="text-muted">{badge.group}</span>
                    {badge.label}
                  </div>
                  <button
                    onClick={badge.onClick}
                    className="text-muted cursor-pointer"
                  >
                    ✕
                  </button>
                </Badge>
              ))}

              {allFilterBadges.length > MAX_VISIBLE_FILTERS && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer rounded bg-[#27272A] px-1.5 py-0.5 text-xs font-semibold flex items-center justify-center">
                      +{allFilterBadges.length - MAX_VISIBLE_FILTERS}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="w-80 p-4 bg-[#18181b] text-colorText border border-[#333]">
                    <h4 className="font-semibold mb-2">{t("sales:filters")}</h4>
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto px-4">
                      {allFilterBadges
                        .slice(MAX_VISIBLE_FILTERS)
                        .map((badge) => (
                          <div
                            key={badge.key}
                            className="flex items-center justify-between text-sm"
                          >
                            <div className="gap-1 flex items-start">
                              <span className="text-muted">{badge.group}</span>
                              <span>{badge.label}</span>
                            </div>
                            <button
                              onClick={badge.onClick}
                              className="text-muted hover:text-white ml-2"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}

              <Button
                variant="secondary"
                onClick={clearAllFilters}
                data-cy="clear-filters"
                className="ml-2 h-7 flex items-center gap-4 bg-transparent text-interactive"
              >
                {t("clear_all_filters")}
              </Button>
            </div>
          )}
        </Card>

        <section className={`grid w-full gap-6 grid-cols-1 ${cardGridCols} mt-6`}>
          <CardCurrency
            text={getSalesPeriodText(dateRange)}
            total={paidSales?.data?.paid?.totals?.count}
            hasTotal
            value={paidSales?.data?.paid?.totals?.amount?.total ?? "R$ 0,00"}
            status="paid"
            isLoading={isLoadingPaidSales}
          />

          {showPendingCard && (
            <CardCurrency
              text={t("sales:pending")}
              total={paidSales?.data?.awaiting_payment?.totals?.count}
              hasTotal
              value={
                paidSales?.data?.awaiting_payment?.totals?.amount?.total ??
                "R$ 0,00"
              }
              status="pending"
              isLoading={isLoadingPaidSales}
            />
          )}

          {showRejectedCard && (
            <CardCurrency
              text={t("sales:rejected")}
              total={paidSales?.data?.not_authorized?.totals?.count}
              hasTotal
              value={
                paidSales?.data?.not_authorized?.totals?.amount?.total ??
                "R$ 0,00"
              }
              isLoading={isLoadingPaidSales}
              status="refused"
            />
          )}

          {showChargebackCard && (
            <CardCurrency
              text={t("common:chargeback")}
              total={paidSales?.data?.chargeback?.totals?.count}
              hasTotal
              value={
                paidSales?.data?.chargeback?.totals?.amount?.total ??
                "R$ 0,00"
              }
              isLoading={isLoadingPaidSales}
              status="chargeback"
            />
          )}

          {showRefundedCard && (
            <CardCurrency
              text={t("common:refunded")}
              total={paidSales?.data?.refunded?.totals?.count}
              hasTotal
              value={
                paidSales?.data?.refunded?.totals?.amount?.total ??
                "R$ 0,00"
              }
              isLoading={isLoadingPaidSales}
              status="refunded"
            />
          )}
        </section>
      </section>

      <section className="mt-6">
        <HourlySalesChart
          width="w-full"
          isLoadingData={isLoadingReports}
          text={getSalesPeriodText(dateRange)}
          hourlySales={reports}
          title={dateAxis === "refunded" ? t("dashboard:refundSales") : undefined}
        />

        <Card className="w-full mt-6 overflow-x-auto bg-[#18181B]">
          <div className="p-4 flex items-start justify-between gap-4 flex-col md:flex-row">
            <h2 className="text-lg font-medium">{t("sales:sales_history")}</h2>
            <ExportButton
              type="orders"
              filters={{
                startDate: exportStartDate,
                endDate: exportEndDate,
                dateAxis,
                filterParams: exportFilterParams,
                q: searchQuery,
                currency,
              }}
              label={t("sales:export")}
              title={t("sales:export_modal.title")}
              variant="secondary"
              className="w-full md:w-auto"
              disabled={sales?.data?.length === 0}
            />
          </div>

          <TableListCustom
            headers={headers}
            isLoadingData={isLoadingSales || isFetchingSales}
            data={{
              data: sales?.data ?? [],
              meta: sales?.meta,
              links: {
                next: sales?.links?.next ?? undefined,
                prev: sales?.links?.prev ?? undefined,
              },
            }}
            currentPage={currentPage}
            datacy="sales-table"
            setCurrentPage={setCurrentPage}
            emptyIconMessage={<Search />}
            emptyTitleMessage={t("noLogs")}
            emptyDescriptionMessage={t("noProductsDescription")}
          >
            {sales?.data?.map((sale, index) => {
              const subscriptionLabel = resolveSaleSubscriptionLabel(sale, t);

              return (
              <TableRow
                key={index}
                data-cy="sales-table"
                onClick={canViewDetails ? () => navigate(`/sales/sales-details/${sale.id}`) : undefined}
                className={`hover:bg-secondary h-16 text-sm border-t border-zinc-800 ${canViewDetails ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <TableCell className="py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[#A1A1AA]">
                      {resolveSaleDisplayDate(sale, { dateAxis })}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-colorText">{sale?.offer?.name}</p>
                  <p className="text-muted">
                    {getOfferTypeLabel(sale?.offer?.type ?? "")}
                  </p>
                  {subscriptionLabel && (
                    <p className="text-xs text-primary">{subscriptionLabel}</p>
                  )}
                </TableCell>
                <TableCell>
                  <p className="text-muted">{sale?.customer?.name}</p>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getIconMethodPayment(
                      sale?.payment_method?.type,
                      sale?.paymentDetail?.credit_card?.brand,
                    )}
                    <span className="text-muted">
                      {t(sale?.payment_method?.type)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-muted">{sale.total_formatted}</p>
                </TableCell>
                <TableCell className="h-full">
                  <div className="items-center inline-block">
                    <BadgeShared status={sale.status} />
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableListCustom>
        </Card>
      </section>
    </LayoutInside>
  );
}